import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FilmSlate, Sparkle, Plus, ArrowRight, ArrowLeft, Trash, Play, PencilSimple,
  ArrowsClockwise, Lock, CheckCircle, Crown, House, X, Pause, SkipForward, SkipBack, SpeakerHigh, SpeakerX, DownloadSimple,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { BackToTop } from '../components/BackToTop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============================================================================
   CINEMATIC STUDIO — Route: /cinematic-studio and /cinematic-studio/:projectId
   ============================================================================ */

export const CinematicStudio = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const paidJustNow = searchParams.get('paid') === '1';
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  if (projectId) {
    return <TrailerWorkspace projectId={projectId} isAdmin={isAdmin} paidJustNow={paidJustNow} />;
  }
  return <StudioDashboard isAdmin={isAdmin} />;
};

/* ============================================================================
   DASHBOARD — list of trailer projects + create new
   ============================================================================ */

const StudioDashboard = ({ isAdmin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectSourceId = searchParams.get('source');
  const [projects, setProjects] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(!!preselectSourceId);
  const [selectedTier, setSelectedTier] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/cinematic/projects`, { withCredentials: true }),
        axios.get(`${API}/cinematic/catalogue`, { withCredentials: true }),
      ]);
      setProjects(p.data.projects || []);
      setCatalogue(c.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickTier = (tierId) => {
    setSelectedTier(tierId);
    setShowCreate(true);
  };

  return (
    <div className="min-h-screen bg-charcoal text-ivory">
      {/* Header */}
      <header className="border-b border-border/60 bg-background/60 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-ash hover:text-ivory" data-testid="cs-back-home">
            <House size={18} weight="duotone" />
            <span className="text-xs uppercase tracking-[0.28em]">Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <FilmSlate size={20} weight="duotone" className="text-gold" />
            <span className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">Cinematic Studio</span>
          </div>
          {isAdmin ? (
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.28em] text-emerald-300"><Crown size={14} weight="duotone" /> Host</div>
          ) : (<div className="w-8" />)}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-3">Bring Your Story To Life</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-light text-ivory mb-4">Cinematic Creative Studio</h1>
          <p className="text-base text-ash font-sans max-w-xl mx-auto">
            Turn your finished work into a cinematic trailer — style, storyboard and stills, crafted in minutes.
          </p>
        </div>

        {/* Choose your plan — always visible entry point */}
        {catalogue && (
          <section className="mb-14" data-testid="cs-plans-section">
            <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">Choose your plan</p>
                <h2 className="text-2xl font-serif text-ivory">Pick the level of cinema for your next trailer</h2>
              </div>
              {isAdmin && (
                <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-300">Complimentary for the host</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {catalogue.tiers.map((t) => {
                const featured = t.id === 'cinematic';
                return (
                  <div
                    key={t.id}
                    className={`text-left border-2 p-6 rounded-lg transition-colors flex flex-col ${featured ? 'border-gold bg-gold/5' : 'border-border bg-stone/30 hover:border-gold/50'}`}
                    data-testid={`cs-tier-card-${t.id}`}
                  >
                    {featured && <p className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2">Most popular</p>}
                    <h3 className="text-lg font-serif text-ivory mb-2">{t.label}</h3>
                    <p className="text-3xl font-serif text-gold mb-4">
                      {isAdmin ? <span className="text-emerald-300 text-xl">Free · Host</span> : <>£{t.amount_gbp.toFixed(0)}</>}
                    </p>
                    <ul className="space-y-1.5 mb-5 flex-1">
                      {t.features.map((f) => (
                        <li key={f} className="text-xs text-bone font-sans flex items-start gap-1.5">
                          <CheckCircle size={12} weight="duotone" className="text-emerald-400 shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => pickTier(t.id)}
                      className={`w-full rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-4 py-3 ${featured ? 'bg-gold text-charcoal hover:bg-gold-dark' : 'bg-transparent border border-gold text-gold hover:bg-gold/10'}`}
                      data-testid={`cs-tier-start-${t.id}`}
                    >
                      Start with {t.label.replace(' Trailer', '').replace(' Package', '')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-serif text-ivory">My Trailer Projects</h2>
          <Button
            onClick={() => { setSelectedTier(null); setShowCreate(true); }}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark inline-flex items-center gap-2"
            data-testid="cs-new-project-btn"
          >
            <Plus size={14} weight="bold" /> New Trailer
          </Button>
        </div>

        {loading ? (
          <p className="text-center text-ash py-16">Loading…</p>
        ) : projects.length === 0 ? (
          <div className="panel-luxe p-12 text-center" data-testid="cs-empty">
            <FilmSlate size={40} weight="duotone" className="text-gold/60 mx-auto mb-4" />
            <p className="text-lg font-serif text-ivory mb-2">No trailers yet</p>
            <p className="text-sm text-ash mb-6">Start with a completed manuscript or a Creative Partner project.</p>
            <Button onClick={() => { setSelectedTier(null); setShowCreate(true); }} className="rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark inline-flex items-center gap-2">
              <Plus size={14} weight="bold" /> Create your first trailer
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <ProjectCard key={p.project_id} project={p} onOpen={() => navigate(`/cinematic-studio/${p.project_id}`)} onDelete={load} />
            ))}
          </div>
        )}
      </main>

      {showCreate && <CreateProjectModal isAdmin={isAdmin} preselectSourceId={preselectSourceId} preselectTier={selectedTier} catalogue={catalogue} onClose={() => setShowCreate(false)} onCreated={(id) => { setShowCreate(false); navigate(`/cinematic-studio/${id}`); }} />}
      <BackToTop />
    </div>
  );
};

const ProjectCard = ({ project, onOpen, onDelete }) => {
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.title}"?`)) return;
    try {
      await axios.delete(`${API}/cinematic/projects/${project.project_id}`, { withCredentials: true });
      toast.success('Deleted');
      onDelete();
    } catch { toast.error('Delete failed'); }
  };
  const stageLabel = { draft: 'Draft', plan_generated: 'Storyboard ready', clips_generated: 'In progress', complete: 'Complete' }[project.status] || project.status;
  return (
    <div
      onClick={onOpen}
      className="panel-luxe p-5 cursor-pointer hover:border-gold/60 transition-colors group"
      data-testid={`cs-project-${project.project_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <FilmSlate size={22} weight="duotone" className="text-gold" />
        <button onClick={remove} className="text-ash hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`cs-delete-${project.project_id}`}>
          <Trash size={14} />
        </button>
      </div>
      <h3 className="text-lg font-serif text-ivory mb-1 truncate">{project.title}</h3>
      <p className="text-[10px] uppercase tracking-[0.24em] text-ash mb-3">
        {stageLabel} · {project.scene_count} scenes
      </p>
      {project.paid ? (
        <p className="text-[10px] uppercase tracking-[0.24em] text-emerald-300 inline-flex items-center gap-1">
          <CheckCircle size={12} weight="duotone" /> Unlocked
        </p>
      ) : (
        <p className="text-[10px] uppercase tracking-[0.24em] text-gold/80 inline-flex items-center gap-1">
          <Lock size={12} weight="duotone" /> Locked
        </p>
      )}
    </div>
  );
};

/* ============================================================================
   CREATE PROJECT MODAL — source-project picker + basic metadata
   ============================================================================ */

const CreateProjectModal = ({ isAdmin: _isAdmin, preselectSourceId, preselectTier, catalogue, onClose, onCreated }) => {
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [selected, setSelected] = useState(null); // {source_type, source_id} | 'manual' | 'upload'
  const [title, setTitle] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [genre, setGenre] = useState('');
  const [tier, setTier] = useState(preselectTier || 'cinematic');
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState('');
  const uploadInputRef = React.useRef(null);

  const pickSource = useCallback(async (s) => {
    setSelected(s);
    if (s === 'manual') { setTitle(''); setSynopsis(''); setGenre(''); setUploadedFilename(''); return; }
    if (s === 'upload') {
      // Trigger the hidden file input immediately
      setUploadedFilename('');
      setTimeout(() => uploadInputRef.current?.click(), 30);
      return;
    }
    try {
      const r = await axios.get(`${API}/cinematic/source-projects/${s.source_type}/${s.source_id}`, { withCredentials: true });
      setTitle(r.data.title || '');
      setSynopsis(r.data.synopsis || '');
      setGenre(r.data.genre || '');
      setUploadedFilename('');
    } catch {
      toast.error('Could not load that project');
    }
  }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    // Client-side size guard so users see an inline error before a round-trip 413
    const MAX_BYTES = 15 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      toast.error(`File too large — max 15 MB (yours is ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
      setSelected(null);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/cinematic/source-projects/upload`, fd, {
        withCredentials: true, timeout: 60000,
      });
      setTitle(r.data.title || '');
      setSynopsis(r.data.synopsis || '');
      setGenre('');
      setUploadedFilename(r.data.source_filename || file.name);
      setSelected('upload');
      toast.success(`Loaded ${r.data.source_filename || file.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      setSelected(null);
    } finally {
      setUploading(false);
      // Reset the input value so the same file can be reselected
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  useEffect(() => {
    axios.get(`${API}/cinematic/source-projects`, { withCredentials: true })
      .then((r) => {
        const list = r.data.projects || [];
        setSources(list);
        // If a source was pre-selected from Dashboard, auto-pick it
        if (preselectSourceId) {
          const match = list.find((s) => s.source_id === preselectSourceId);
          if (match) pickSource(match);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingSources(false));
  }, [preselectSourceId, pickSource]);

  const create = async () => {
    if (!title.trim()) { toast.error('Please give the trailer a title'); return; }
    setCreating(true);
    try {
      const payload = {
        title: title.trim(),
        synopsis: synopsis.trim(),
        genre: genre.trim(),
        tier,
        source_type: selected === 'manual' ? 'manual' : (selected?.source_type || 'manual'),
        source_id: selected === 'manual' ? null : (selected?.source_id || null),
      };
      const r = await axios.post(`${API}/cinematic/projects`, payload, { withCredentials: true });
      toast.success('Trailer project created');
      onCreated(r.data.project_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create project');
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/85 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="cs-create-modal">
      <div className="bg-background border border-border max-w-2xl w-full rounded-lg shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border/60">
          <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">
            {preselectTier
              ? `${(catalogue?.tiers || []).find((t) => t.id === preselectTier)?.label || 'Trailer'} — pick your story`
              : 'Step 1 · Select Project'}
          </p>
          <h3 className="text-2xl font-serif text-ivory">Choose the story to bring to life</h3>
        </div>

        <div className="p-6 overflow-y-auto">
          {/* Source list */}
          {loadingSources ? (
            <p className="text-sm text-ash">Loading your projects…</p>
          ) : (
            <div className="space-y-2 mb-5">
              {sources.map((s) => (
                <button
                  key={`${s.source_type}-${s.source_id}`}
                  onClick={() => pickSource(s)}
                  className={`w-full text-left border rounded-lg p-3 transition-colors ${selected && selected !== 'manual' && selected.source_id === s.source_id ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                  data-testid={`cs-source-${s.source_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-serif text-ivory">{s.label}</span>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-ash">
                      {s.source_type === 'proofread' ? 'Manuscript' : 'Creative Partner'}
                    </span>
                  </div>
                  {s.preview && <p className="text-xs text-ash line-clamp-2">{s.preview}</p>}
                </button>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={() => pickSource('manual')}
                  className={`text-left border rounded-lg p-3 transition-colors ${selected === 'manual' ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                  data-testid="cs-source-manual"
                >
                  <div className="text-sm font-serif text-ivory">✍️ Start from scratch</div>
                  <p className="text-xs text-ash">Enter title + synopsis manually.</p>
                </button>
                <button
                  type="button"
                  onClick={() => pickSource('upload')}
                  disabled={uploading}
                  className={`text-left border rounded-lg p-3 transition-colors disabled:opacity-60 ${selected === 'upload' ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                  data-testid="cs-source-upload"
                >
                  <div className="text-sm font-serif text-ivory">📁 Upload a manuscript</div>
                  <p className="text-xs text-ash">
                    {uploading
                      ? 'Reading your file…'
                      : uploadedFilename
                        ? `Loaded ${uploadedFilename}`
                        : '.txt / .md / .docx / .pdf / .fdx / .fountain'}
                  </p>
                </button>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".txt,.md,.docx,.pdf,.fdx,.fountain"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files?.[0])}
                  data-testid="cs-source-upload-input"
                />
              </div>
            </div>
          )}

          {/* Metadata */}
          {selected && (
            <div className="space-y-3 pt-4 border-t border-border/60" data-testid="cs-metadata">
              <div>
                <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1 block">Trailer title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The Silent Tide" className="w-full bg-stone/40 border border-border rounded-md text-ivory font-serif text-lg p-2.5 focus:outline-none focus:border-gold" data-testid="cs-title-input" />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1 block">Synopsis (auto-filled)</label>
                <textarea value={synopsis} onChange={(e) => setSynopsis(e.target.value)} rows={4} maxLength={1500} className="w-full bg-stone/40 border border-border rounded-md text-bone font-sans text-sm p-2.5 focus:outline-none focus:border-gold" data-testid="cs-synopsis-input" />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1 block">Genre (optional)</label>
                <input value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="Thriller, Fantasy, Romance…" className="w-full bg-stone/40 border border-border rounded-md text-bone font-sans text-sm p-2.5 focus:outline-none focus:border-gold" data-testid="cs-genre-input" />
              </div>
              {catalogue && (
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2 block">Plan</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="cs-modal-tier-toggle">
                    {catalogue.tiers.map((t) => {
                      const active = tier === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setTier(t.id)}
                          className={`text-left border rounded-md p-2 transition-colors ${active ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                          data-testid={`cs-modal-tier-${t.id}`}
                        >
                          <div className={`text-[10px] font-serif ${active ? 'text-ivory' : 'text-bone'}`}>{t.label.replace(' Trailer', '').replace(' Package', '')}</div>
                          <div className="text-[10px] text-gold">£{t.amount_gbp.toFixed(0)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border/60 flex justify-between items-center">
          <button onClick={onClose} className="text-xs uppercase tracking-[0.24em] text-ash hover:text-ivory">Cancel</button>
          <Button onClick={create} disabled={!selected || creating || !title.trim()} className="rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-6 py-3 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50 inline-flex items-center gap-2" data-testid="cs-create-project-btn">
            {creating ? 'Creating…' : 'Continue'} <ArrowRight size={12} weight="bold" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   TRAILER WORKSPACE — style pickers → plan → storyboard → clips
   ============================================================================ */

const TrailerWorkspace = ({ projectId, isAdmin, paidJustNow }) => {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/cinematic/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${API}/cinematic/catalogue`, { withCredentials: true }),
      ]);
      setProject(p.data);
      setCatalogue(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load project');
      navigate('/cinematic-studio');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (paidJustNow) toast.success('Trailer unlocked — let\'s make some magic'); }, [paidJustNow]);

  const savePartial = async (updates) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/cinematic/projects/${projectId}`, updates, { withCredentials: true });
      setProject(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const generatePlan = async () => {
    if (!project?.cinematic_style || !project?.director_style) { toast.error('Please pick a cinematic style AND a director style first.'); return; }
    setGenerating(true);
    try {
      // Kick off background job — returns immediately with { status: 'processing' }
      await axios.post(`${API}/cinematic/projects/${projectId}/generate-plan`, {}, { withCredentials: true, timeout: 15000 });
      // Poll every 3s until complete/error (max ~3 min)
      const started = Date.now();
      const maxWait = 180000;
      while (Date.now() - started < maxWait) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await axios.get(`${API}/cinematic/projects/${projectId}/generate-plan/status`, { withCredentials: true, timeout: 10000 });
        if (st.data.status === 'complete') {
          setProject(st.data.project);
          toast.success('Storyboard drafted');
          return;
        }
        if (st.data.status === 'error') {
          toast.error(st.data.error || 'Trailer plan generation failed');
          return;
        }
      }
      toast.error('Still working — please refresh in a moment.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Trailer plan generation failed');
    } finally { setGenerating(false); }
  };

  if (loading || !project || !catalogue) return (
    <div className="min-h-screen bg-charcoal text-ivory flex items-center justify-center">
      <p className="text-sm text-ash">Loading trailer studio…</p>
    </div>
  );

  const locked = !project.paid && !isAdmin;

  return (
    <div className="min-h-screen bg-charcoal text-ivory">
      <header className="border-b border-border/60 bg-background/60 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/cinematic-studio')} className="flex items-center gap-2 text-ash hover:text-ivory" data-testid="cs-back">
            <ArrowLeft size={16} weight="bold" /> <span className="text-xs uppercase tracking-[0.24em]">All Trailers</span>
          </button>
          <div className="flex items-center gap-2">
            <FilmSlate size={18} weight="duotone" className="text-gold" />
            <span className="text-sm font-serif text-ivory truncate max-w-xs">{project.title}</span>
          </div>
          <div className="w-16 text-right">
            {saving && <span className="text-[10px] uppercase tracking-[0.24em] text-ash">Saving…</span>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* Payment gate */}
        {locked && (
          <PaymentGate project={project} catalogue={catalogue} onPaid={load} />
        )}

        {!locked && (
          <>
            {/* Style pickers */}
            <StepStyle
              catalogue={catalogue}
              project={project}
              onSave={savePartial}
            />

            {/* Generate plan */}
            <div className="flex items-center justify-between panel-luxe p-6">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">Step 3 · AI Trailer Plan</p>
                <p className="text-sm text-bone">Let the studio draft a logline + full storyboard.</p>
              </div>
              <Button
                onClick={generatePlan}
                disabled={generating || !project.cinematic_style || !project.director_style}
                className="rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="cs-generate-plan-btn"
              >
                {generating ? 'Crafting…' : (project.trailer_plan ? 'Regenerate Plan' : 'Generate Plan')} <Sparkle size={12} weight="bold" />
              </Button>
            </div>

            {/* Storyboard + clips */}
            {project.trailer_plan && project.scenes?.length > 0 && (
              <>
                <VideoSettings project={project} catalogue={catalogue} onSave={savePartial} />
                <StoryboardSection
                  project={project}
                  projectId={projectId}
                  onSceneUpdated={load}
                  isAdmin={isAdmin}
                  maxRegenerations={
                    catalogue.tiers.find((t) => t.id === (project.tier || 'cinematic'))?.max_regenerations || 3
                  }
                />
              </>
            )}
          </>
        )}
      </main>
      <BackToTop />
    </div>
  );
};

/* ---------- Sub-sections ---------- */

const StepStyle = ({ catalogue, project, onSave }) => (
  <div className="panel-luxe p-6 space-y-6">
    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">Step 2a · Cinematic Style</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3" data-testid="cs-style-grid">
        {catalogue.cinematic_styles.map((s) => {
          const active = project.cinematic_style === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onSave({ cinematic_style: s.id })}
              className={`text-left rounded-lg border p-3 transition-colors ${active ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
              data-testid={`cs-style-${s.id}`}
            >
              <div className="text-lg mb-1">{s.emoji}</div>
              <div className={`text-xs font-serif ${active ? 'text-ivory' : 'text-bone'}`}>{s.label}</div>
              <div className="text-[10px] text-ash mt-1 leading-relaxed">{s.hint}</div>
            </button>
          );
        })}
      </div>
    </div>

    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">Step 2b · Director Style</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3" data-testid="cs-director-grid">
        {catalogue.director_styles.map((d) => {
          const active = project.director_style === d.id;
          return (
            <button
              key={d.id}
              onClick={() => onSave({ director_style: d.id })}
              className={`text-left rounded-lg border p-4 transition-colors ${active ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
              data-testid={`cs-director-${d.id}`}
            >
              <div className={`text-sm font-serif ${active ? 'text-ivory' : 'text-bone'} mb-1`}>{d.label}</div>
              <div className="text-[10px] text-ash leading-relaxed">{d.hint}</div>
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

const StoryboardSection = ({ project, onSceneUpdated, isAdmin, maxRegenerations, projectId }) => {
  const plan = project.trailer_plan || {};
  const scenes = project.scenes || [];
  const total = scenes.length;
  const ready = scenes.filter((s) => s.image_data_b64).length;
  const allReady = total > 0 && ready === total;
  const voScenes = scenes.filter((s) => (s.voice_over || '').trim()).length;
  const voReady = scenes.filter((s) => s.voiceover_data_b64).length;
  const voComplete = voScenes > 0 && voReady === voScenes;

  const [batching, setBatching] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [voBusy, setVoBusy] = useState(false);
  const [voProgress, setVoProgress] = useState(null);
  const [renderBusy, setRenderBusy] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [trailerMode, setTrailerMode] = useState('stills'); // 'stills' | 'clips'
  const [bibleBusy, setBibleBusy] = useState(false);
  const [bibleEditing, setBibleEditing] = useState(false);
  const [bibleDraft, setBibleDraft] = useState('');
  const [settingBusy, setSettingBusy] = useState(false);
  const [settingEditing, setSettingEditing] = useState(false);
  const [settingDraft, setSettingDraft] = useState('');
  const [rewriteBusy, setRewriteBusy] = useState(false);
  const [batchClipBusy, setBatchClipBusy] = useState(false);
  const [batchClipStatus, setBatchClipStatus] = useState(null);
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);

  // Check for existing rendered video on mount
  useEffect(() => {
    axios.get(`${API}/cinematic/projects/${projectId}/video-status`, { withCredentials: true })
      .then((r) => { if (r.data.status === 'complete') setVideoReady(true); })
      .catch(() => {});
  }, [projectId]);

  const downloadPackage = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/cinematic/projects/${projectId}/download`, {
        withCredentials: true,
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const safe = (project.title || 'trailer').replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${safe}_trailer_package.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Trailer package downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const generateBible = async () => {
    setBibleBusy(true);
    try {
      const r = await axios.post(`${API}/cinematic/projects/${projectId}/character-bible/generate`, {}, { withCredentials: true, timeout: 90000 });
      toast.success('Character bible locked in');
      setBibleDraft(r.data.character_bible || '');
      onSceneUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Character bible generation failed');
    } finally { setBibleBusy(false); }
  };

  const saveBible = async () => {
    setBibleBusy(true);
    try {
      await axios.patch(`${API}/cinematic/projects/${projectId}/character-bible`,
        { character_bible: bibleDraft }, { withCredentials: true, timeout: 15000 });
      toast.success('Character bible saved');
      setBibleEditing(false);
      onSceneUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBibleBusy(false); }
  };

  const clearBible = async () => {
    setBibleBusy(true);
    try {
      await axios.patch(`${API}/cinematic/projects/${projectId}/character-bible`,
        { character_bible: '' }, { withCredentials: true, timeout: 15000 });
      toast.success('Character bible cleared');
      setBibleDraft('');
      setBibleEditing(false);
      onSceneUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clear failed');
    } finally { setBibleBusy(false); }
  };

  // --- Setting Bible (world/location lock) ---
  const generateSetting = async () => {
    setSettingBusy(true);
    try {
      const r = await axios.post(`${API}/cinematic/projects/${projectId}/setting-bible/generate`, {}, { withCredentials: true, timeout: 90000 });
      toast.success('Setting bible locked in');
      setSettingDraft(r.data.setting_bible || '');
      onSceneUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Setting bible generation failed');
    } finally { setSettingBusy(false); }
  };
  const saveSetting = async () => {
    setSettingBusy(true);
    try {
      await axios.patch(`${API}/cinematic/projects/${projectId}/setting-bible`,
        { setting_bible: settingDraft }, { withCredentials: true, timeout: 15000 });
      toast.success('Setting bible saved');
      setSettingEditing(false);
      onSceneUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSettingBusy(false); }
  };
  const clearSetting = async () => {
    setSettingBusy(true);
    try {
      await axios.patch(`${API}/cinematic/projects/${projectId}/setting-bible`,
        { setting_bible: '' }, { withCredentials: true, timeout: 15000 });
      toast.success('Setting bible cleared');
      setSettingDraft('');
      setSettingEditing(false);
      onSceneUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clear failed');
    } finally { setSettingBusy(false); }
  };

  // --- Story-grounded Sora prompt rewrite (uses manuscript + bibles) ---
  const rewriteSoraPrompts = async () => {
    setRewriteBusy(true);
    try {
      const r = await axios.post(`${API}/cinematic/projects/${projectId}/scenes/rewrite-sora-prompts`, {}, { withCredentials: true, timeout: 120000 });
      toast.success(`Rewrote ${r.data.rewritten} scene prompts using manuscript excerpts`);
      onSceneUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rewrite failed');
    } finally { setRewriteBusy(false); }
  };

  const generateAllClips = async () => {
    setBatchConfirmOpen(false);
    setBatchClipBusy(true);
    try {
      await axios.post(`${API}/cinematic/projects/${projectId}/scenes/generate-all-clips`,
        { provider: 'sora2_text', duration: 4, model: 'sora-2' },
        { withCredentials: true, timeout: 15000 });
      toast.success('Sora clip batch started — this can take several minutes.');
      const started = Date.now();
      while (Date.now() - started < 30 * 60 * 1000) { // 30 min ceiling
        await new Promise((r) => setTimeout(r, 8000));
        const st = await axios.get(`${API}/cinematic/projects/${projectId}/scenes/clip-batch-status`, { withCredentials: true, timeout: 10000 });
        setBatchClipStatus(st.data);
        onSceneUpdated();
        if (st.data.status === 'complete') {
          const done = st.data.batch_complete || 0;
          const failed = st.data.batch_failed || 0;
          if (failed > 0) toast.warning(`Batch done: ${done} succeeded, ${failed} failed. Retry failed ones individually.`);
          else toast.success(`All ${done} Sora clips generated`);
          return;
        }
        if (st.data.status === 'error') {
          toast.error(st.data.error || 'Batch failed');
          return;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch clip generation failed');
    } finally { setBatchClipBusy(false); }
  };

  const generateVoiceOvers = async () => {
    setVoBusy(true);
    try {
      await axios.post(`${API}/cinematic/projects/${projectId}/generate-voiceovers`, {}, { withCredentials: true, timeout: 15000 });
      toast.success('Recording narration…');
      const started = Date.now();
      while (Date.now() - started < 300000) { // up to 5 min
        await new Promise((r) => setTimeout(r, 4000));
        const st = await axios.get(`${API}/cinematic/projects/${projectId}/voiceover-status`, { withCredentials: true, timeout: 10000 });
        setVoProgress(st.data);
        onSceneUpdated();
        if (st.data.status === 'complete') {
          toast.success('Narration ready');
          return;
        }
        if (st.data.status === 'error') {
          toast.error(st.data.error || 'Voice-over failed');
          return;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Voice-over failed');
    } finally { setVoBusy(false); }
  };

  const renderVideo = async (mode = 'stills') => {
    setRenderBusy(true);
    setVideoReady(false);
    try {
      await axios.post(`${API}/cinematic/projects/${projectId}/render-video`, { mode }, { withCredentials: true, timeout: 15000 });
      toast.success(mode === 'clips'
        ? 'Rendering your video trailer with Sora clips — this can take a minute or two…'
        : 'Rendering your picture trailer — this can take a minute or two…');
      const started = Date.now();
      while (Date.now() - started < 600000) {
        await new Promise((r) => setTimeout(r, 5000));
        const st = await axios.get(`${API}/cinematic/projects/${projectId}/video-status`, { withCredentials: true, timeout: 10000 });
        if (st.data.status === 'complete') {
          setVideoReady(true);
          toast.success('MP4 trailer is ready to download');
          return;
        }
        if (st.data.status === 'error') {
          toast.error(st.data.error || 'Video render failed');
          return;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Video render failed');
    } finally { setRenderBusy(false); }
  };

  const downloadVideo = () => {
    const safe = (project.title || 'trailer').replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
    const link = document.createElement('a');
    link.href = `${API}/cinematic/projects/${projectId}/video`;
    link.download = `${safe}_trailer.mp4`;
    link.click();
  };

  const generateAll = async () => {
    setBatching(true);
    try {
      await axios.post(`${API}/cinematic/projects/${projectId}/generate-all-stills`, {}, { withCredentials: true, timeout: 15000 });
      toast.success('Generating all scenes — this may take a couple of minutes…');
      // Poll batch status
      const started = Date.now();
      while (Date.now() - started < 600000) { // up to 10 min
        await new Promise((r) => setTimeout(r, 5000));
        const st = await axios.get(`${API}/cinematic/projects/${projectId}/batch-status`, { withCredentials: true, timeout: 10000 });
        setBatchProgress(st.data);
        if (st.data.all_ready) {
          onSceneUpdated();
          toast.success('Your trailer is ready to play');
          return;
        }
        if (st.data.in_progress === 0 && st.data.complete < st.data.total) {
          // No workers running — refresh from server
          onSceneUpdated();
          break;
        }
        // Refresh scenes periodically so partial renders show up
        onSceneUpdated();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch generation failed');
    } finally {
      setBatching(false);
    }
  };

  return (
    <div className="panel-luxe p-6 space-y-6" data-testid="cs-storyboard">
      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">Storyboard</p>
        {plan.logline && (
          <blockquote className="border-l-2 border-gold pl-4 mt-3 mb-3">
            <p className="text-xl font-serif text-ivory italic">&ldquo;{plan.logline}&rdquo;</p>
          </blockquote>
        )}
        {plan.concept && <p className="text-sm text-bone font-sans">{plan.concept}</p>}
      </div>

      {/* Character Bible — locked protagonist description injected into every Sora prompt.
          Only surfaced to admins today; it's only useful when Sora clips can be generated,
          which is gated to admins for cost reasons. Un-gate this alongside Sora when the
          Cinematic Producer plan launches. */}
      {isAdmin && (
      <div className="rounded-lg border border-border bg-stone/20 p-5" data-testid="cs-character-bible">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={12} weight="bold" className="text-gold" />
              <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">Cast · Locked Protagonist</p>
              {(project.character_bible || '').trim() && <CheckCircle size={12} weight="duotone" className="text-emerald-400" />}
            </div>
            <p className="text-xs text-ash mb-3">
              Locks the main character&apos;s face, age and wardrobe so every Sora clip matches. Optional, but strongly recommended before batch-generating clips.
            </p>
            {bibleEditing ? (
              <textarea
                value={bibleDraft}
                onChange={(e) => setBibleDraft(e.target.value.slice(0, 300))}
                rows={3}
                maxLength={300}
                placeholder="e.g. Emma Blake, 32, athletic, cropped auburn hair, olive skin, navy trench coat over cream turtleneck, faint scar above left eyebrow."
                className="w-full bg-charcoal/60 border border-border rounded-md px-3 py-2 text-sm text-ivory font-sans focus:outline-none focus:border-gold/70"
                data-testid="cs-bible-textarea"
              />
            ) : (
              <p className="text-sm text-ivory font-serif italic leading-relaxed" data-testid="cs-bible-display">
                {(project.character_bible || '').trim()
                  ? `\u201C${project.character_bible}\u201D`
                  : <span className="text-ash not-italic font-sans">No character locked yet.</span>}
              </p>
            )}
            {bibleEditing && (
              <p className="text-[10px] text-ash mt-1">{bibleDraft.length} / 300 chars</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {!bibleEditing && (
              <Button
                onClick={generateBible}
                disabled={bibleBusy}
                className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="cs-bible-generate-btn"
              >
                <Sparkle size={11} weight="bold" /> {bibleBusy ? 'Working…' : (project.character_bible ? 'Regenerate' : 'Auto-generate')}
              </Button>
            )}
            {!bibleEditing && (
              <Button
                onClick={() => { setBibleDraft(project.character_bible || ''); setBibleEditing(true); }}
                disabled={bibleBusy}
                className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-border text-bone hover:border-gold/40 disabled:opacity-50"
                data-testid="cs-bible-edit-btn"
              >
                Edit
              </Button>
            )}
            {bibleEditing && (
              <>
                <Button
                  onClick={saveBible}
                  disabled={bibleBusy}
                  className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50"
                  data-testid="cs-bible-save-btn"
                >
                  Save
                </Button>
                <Button
                  onClick={() => { setBibleEditing(false); setBibleDraft(project.character_bible || ''); }}
                  disabled={bibleBusy}
                  className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-border text-bone hover:border-gold/40 disabled:opacity-50"
                  data-testid="cs-bible-cancel-btn"
                >
                  Cancel
                </Button>
              </>
            )}
            {(project.character_bible || '').trim() && !bibleEditing && (
              <Button
                onClick={clearBible}
                disabled={bibleBusy}
                className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-border text-ash hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-50"
                data-testid="cs-bible-clear-btn"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Setting Bible + Sora prompt rewrite — admin-only, mirrors Character Bible */}
      {isAdmin && (
      <div className="rounded-lg border border-border bg-stone/20 p-5" data-testid="cs-setting-bible">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={12} weight="bold" className="text-gold" />
              <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">World · Locked Setting</p>
              {(project.setting_bible || '').trim() && <CheckCircle size={12} weight="duotone" className="text-emerald-400" />}
            </div>
            <p className="text-xs text-ash mb-3">
              Locks era, location, weather and colour palette so every Sora clip lives in the same world. Combines with Character Bible for maximum consistency.
            </p>
            {settingEditing ? (
              <textarea
                value={settingDraft}
                onChange={(e) => setSettingDraft(e.target.value.slice(0, 300))}
                rows={3}
                maxLength={300}
                placeholder="e.g. Victorian Edinburgh, October 1890s. Bone-chilling fog. Amber gaslight, wet cobblestones, shadows charcoal and black."
                className="w-full bg-charcoal/60 border border-border rounded-md px-3 py-2 text-sm text-ivory font-sans focus:outline-none focus:border-gold/70"
                data-testid="cs-setting-textarea"
              />
            ) : (
              <p className="text-sm text-ivory font-serif italic leading-relaxed" data-testid="cs-setting-display">
                {(project.setting_bible || '').trim()
                  ? `\u201C${project.setting_bible}\u201D`
                  : <span className="text-ash not-italic font-sans">No setting locked yet.</span>}
              </p>
            )}
            {settingEditing && <p className="text-[10px] text-ash mt-1">{settingDraft.length} / 300 chars</p>}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {!settingEditing && (
              <Button onClick={generateSetting} disabled={settingBusy} className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="cs-setting-generate-btn">
                <Sparkle size={11} weight="bold" /> {settingBusy ? 'Working…' : (project.setting_bible ? 'Regenerate' : 'Auto-generate')}
              </Button>
            )}
            {!settingEditing && (
              <Button onClick={() => { setSettingDraft(project.setting_bible || ''); setSettingEditing(true); }} disabled={settingBusy} className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-border text-bone hover:border-gold/40 disabled:opacity-50" data-testid="cs-setting-edit-btn">Edit</Button>
            )}
            {settingEditing && (
              <>
                <Button onClick={saveSetting} disabled={settingBusy} className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50" data-testid="cs-setting-save-btn">Save</Button>
                <Button onClick={() => { setSettingEditing(false); setSettingDraft(project.setting_bible || ''); }} disabled={settingBusy} className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-border text-bone hover:border-gold/40 disabled:opacity-50" data-testid="cs-setting-cancel-btn">Cancel</Button>
              </>
            )}
            {(project.setting_bible || '').trim() && !settingEditing && (
              <Button onClick={clearSetting} disabled={settingBusy} className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-3 py-2 bg-transparent border border-border text-ash hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-50" data-testid="cs-setting-clear-btn">Clear</Button>
            )}
          </div>
        </div>

        {/* Story-grounded Sora prompts — one-click regeneration from the actual manuscript */}
        <div className="mt-4 pt-4 border-t border-border/50 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex-1 min-w-[200px]">
            <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1">Story-grounded Sora prompts</p>
            <p className="text-xs text-ash">Rewrites every scene&apos;s Sora prompt from your manuscript so clips reflect real story beats — not generic mood shots.</p>
          </div>
          <Button onClick={rewriteSoraPrompts} disabled={rewriteBusy} className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50 inline-flex items-center gap-1.5 shrink-0" data-testid="cs-rewrite-sora-btn">
            <Sparkle size={11} weight="bold" /> {rewriteBusy ? 'Rewriting…' : 'Rewrite from Manuscript'}
          </Button>
        </div>
      </div>
      )}

      {/* Master action bar — Generate All / Play Trailer */}
      <div className="rounded-lg border-2 border-gold/40 bg-gold/5 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="cs-master-actions">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">
            {allReady ? 'Step 5 · Trailer Ready' : 'Step 4 · Bring It To Life'}
          </p>
          <p className="text-sm text-bone font-sans">
            {allReady
              ? 'All scenes rendered. Press Play for the full cinematic experience.'
              : `${ready} of ${total} scenes rendered${batching ? ' — working now…' : '. Generate the rest in one click.'}`}
          </p>
          {batchProgress?.failed > 0 && (
            <p className="text-xs text-rose-400 mt-1" data-testid="cs-batch-failed">
              {batchProgress.failed} scene{batchProgress.failed === 1 ? '' : 's'} failed — click Generate all again to retry.
            </p>
          )}
          {(batching || batchProgress) && !allReady && (
            <div className="mt-3 w-64 h-1.5 bg-stone rounded-full overflow-hidden">
              <div className="h-full bg-gold transition-all duration-500" style={{ width: `${Math.round(((batchProgress?.complete || ready) / total) * 100)}%` }} />
            </div>
          )}
        </div>
        {allReady ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={downloadPackage}
              disabled={downloading}
              className="rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-5 py-4 bg-transparent border border-gold text-gold hover:bg-gold/10 inline-flex items-center gap-2"
              data-testid="cs-download-btn"
            >
              <DownloadSimple size={14} weight="duotone" /> {downloading ? 'Preparing…' : 'Download package'}
            </Button>
            <Button
              onClick={() => setPlayerOpen(true)}
              className="rounded-lg font-medium text-sm uppercase tracking-[0.24em] px-8 py-4 bg-gold text-charcoal hover:bg-gold-dark inline-flex items-center gap-2"
              data-testid="cs-play-trailer-btn"
            >
              <Play size={16} weight="fill" /> Play Trailer
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            {ready > 0 && (
              <Button
                onClick={downloadPackage}
                disabled={downloading}
                className="rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-5 py-4 bg-transparent border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center gap-2"
                data-testid="cs-download-partial-btn"
                title="Download whatever is rendered so far"
              >
                <DownloadSimple size={14} weight="duotone" /> {downloading ? 'Preparing…' : `Download (${ready})`}
              </Button>
            )}
            <Button
              onClick={generateAll}
              disabled={batching}
              className="rounded-lg font-medium text-sm uppercase tracking-[0.24em] px-6 py-4 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="cs-generate-all-btn"
            >
              {batching ? 'Rendering…' : <><Sparkle size={14} weight="bold" /> Generate all scenes</>}
            </Button>
          </div>
        )}
      </div>

      {/* Post-production — voice-over + MP4 render, shown once at least one scene is ready */}
      {ready > 0 && (
        <div className="rounded-lg border border-border bg-stone/30 p-5 space-y-4" data-testid="cs-post-production">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">Post-production</p>
            <p className="text-sm text-bone font-sans">Add narration and render your trailer as a shareable MP4.</p>
          </div>

          <div className={`grid grid-cols-1 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            {/* Voice-over */}
            <div className="rounded-md border border-border bg-charcoal/40 p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <SpeakerHigh size={16} weight="duotone" className="text-gold" />
                <span className="text-sm font-serif text-ivory">Narration</span>
                {voComplete && <CheckCircle size={14} weight="duotone" className="text-emerald-400 ml-auto" />}
              </div>
              <p className="text-xs text-ash mb-3 flex-1">
                {voScenes === 0
                  ? 'No voice-over lines in your storyboard.'
                  : voComplete
                    ? `Cinematic narration recorded for all ${voScenes} scenes.`
                    : `${voReady} of ${voScenes} narration lines recorded${voBusy ? ' — recording now…' : ''}.`}
              </p>
              {voProgress?.total > 0 && voBusy && (
                <div className="mb-3 w-full h-1 bg-stone rounded-full overflow-hidden">
                  <div className="h-full bg-gold transition-all duration-500" style={{ width: `${Math.round((voProgress.progress / voProgress.total) * 100)}%` }} />
                </div>
              )}
              <Button
                onClick={generateVoiceOvers}
                disabled={voBusy || voScenes === 0}
                className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 bg-transparent border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50 inline-flex items-center gap-2 self-start"
                data-testid="cs-generate-voiceover-btn"
              >
                {voBusy ? 'Recording…' : voComplete ? 'Re-record narration' : 'Record narration'}
              </Button>
            </div>

            {/* MP4 render */}
            <div className="rounded-md border border-border bg-charcoal/40 p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <FilmSlate size={16} weight="duotone" className="text-gold" />
                <span className="text-sm font-serif text-ivory">Shareable MP4</span>
                {videoReady && <CheckCircle size={14} weight="duotone" className="text-emerald-400 ml-auto" />}
              </div>
              <p className="text-xs text-ash mb-3 flex-1">
                {videoReady
                  ? 'Trailer video is ready to download for Instagram, TikTok and X.'
                  : renderBusy
                    ? 'Rendering — this can take a minute or two…'
                    : 'Stitch every scene into a real video file with Ken-Burns and narration.'}
              </p>
              {(() => {
                const clipCount = scenes.filter((s) => s.clip_file).length;
                const canRenderClips = clipCount > 0;
                // Only surface the Picture / Video toggle to admins today —
                // Sora generation is admin-only for cost reasons, so a paying
                // customer would only see a permanently-disabled "Video trailer"
                // button which reads as a broken feature. Non-admins get the
                // classic Ken Burns picture-trailer flow with no choice UI.
                if (!isAdmin) return null;
                return (
                  <div className="mb-3" data-testid="cs-trailer-style-toggle">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-ash mb-1.5">Trailer style</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        onClick={() => setTrailerMode('stills')}
                        disabled={renderBusy}
                        className={`text-left px-2.5 py-2 rounded-md border text-[11px] transition ${trailerMode === 'stills' ? 'border-gold bg-gold/10 text-ivory' : 'border-border text-ash hover:border-gold/40'}`}
                        data-testid="cs-mode-stills"
                      >
                        <div className="font-semibold text-[11px] mb-0.5">Picture trailer</div>
                        <div className="text-[9px] text-ash leading-tight">Ken Burns on stills — cheapest</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => canRenderClips && setTrailerMode('clips')}
                        disabled={renderBusy || !canRenderClips}
                        title={canRenderClips ? '' : 'Generate at least one Sora clip first (Try Sora on a scene card)'}
                        className={`text-left px-2.5 py-2 rounded-md border text-[11px] transition ${trailerMode === 'clips' && canRenderClips ? 'border-gold bg-gold/10 text-ivory' : 'border-border text-ash hover:border-gold/40'} ${!canRenderClips ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid="cs-mode-clips"
                      >
                        <div className="font-semibold text-[11px] mb-0.5">Video trailer {canRenderClips ? `· ${clipCount}` : ''}</div>
                        <div className="text-[9px] text-ash leading-tight">
                          {canRenderClips ? 'Sora clips + Ken Burns fallback' : 'Needs at least 1 Sora clip'}
                        </div>
                      </button>
                    </div>
                  </div>
                );
              })()}
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => renderVideo(trailerMode)}
                  disabled={renderBusy || !allReady}
                  className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 bg-transparent border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="cs-render-video-btn"
                  title={!allReady ? 'Render all scenes first' : ''}
                >
                  {renderBusy ? 'Rendering…' : videoReady ? 'Re-render MP4' : 'Render MP4'}
                </Button>
                {videoReady && (
                  <Button
                    onClick={downloadVideo}
                    className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 bg-gold text-charcoal hover:bg-gold-dark inline-flex items-center gap-2"
                    data-testid="cs-download-video-btn"
                  >
                    <DownloadSimple size={12} weight="bold" /> Download MP4
                  </Button>
                )}
              </div>
              {!allReady && !videoReady && (
                <p className="text-[10px] text-ash mt-2">Render all {total} scenes to unlock MP4 export.</p>
              )}
            </div>

            {/* Auto Video-Trailer — batch Sora clip generation (admin POC only) */}
            {isAdmin && (() => {
              // Sora batch fires ONLY for opted-in scenes (Iter 35, Option B).
              const optedInScenes = scenes.filter((s) => s.include_in_video_trailer);
              const optedInCount = optedInScenes.length;
              const optedInReady = optedInScenes.filter((s) => s.clip_file).length;
              const pending = optedInCount - optedInReady;
              const batchComplete = batchClipStatus?.batch_complete || 0;
              const batchTotal = batchClipStatus?.pending_batch_total || pending;
              const cur = typeof batchClipStatus?.current_scene === 'number' ? batchClipStatus.current_scene + 1 : null;
              return (
                <div className="rounded-md border border-border bg-charcoal/40 p-4 flex flex-col" data-testid="cs-batch-sora">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkle size={16} weight="duotone" className="text-gold" />
                    <span className="text-sm font-serif text-ivory">Sora video clips</span>
                    <span className="text-[9px] uppercase tracking-[0.24em] text-ash ml-auto">POC · host only</span>
                  </div>
                  <p className="text-xs text-ash mb-3 flex-1">
                    {batchClipBusy
                      ? `Rendering clip ${cur || '?'} of ${batchTotal}. Each Sora clip runs 60-180s.`
                      : optedInCount === 0
                        ? 'Tick "Use this scene as real motion" on the scene cards you want as real Sora clips, then come back here.'
                        : pending === 0
                          ? `All ${optedInCount} opted-in scene${optedInCount === 1 ? '' : 's'} clipped. Switch trailer style to Video to use them.`
                          : `${optedInReady} of ${optedInCount} opted-in scene${optedInCount === 1 ? '' : 's'} clipped. Batch-render the rest in one click.`}
                  </p>
                  {batchClipBusy && batchTotal > 0 && (
                    <div className="mb-3 w-full h-1 bg-stone rounded-full overflow-hidden">
                      <div className="h-full bg-gold transition-all duration-500" style={{ width: `${Math.round((batchComplete / batchTotal) * 100)}%` }} />
                    </div>
                  )}
                  <Button
                    onClick={() => setBatchConfirmOpen(true)}
                    disabled={batchClipBusy || pending === 0}
                    className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 bg-transparent border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50 inline-flex items-center gap-2 self-start"
                    data-testid="cs-batch-sora-btn"
                  >
                    {batchClipBusy
                      ? 'Rendering…'
                      : optedInCount === 0
                        ? 'Opt in scenes first'
                        : pending === 0
                          ? 'All clips done'
                          : <><Sparkle size={11} weight="bold" /> Generate {pending} clip{pending === 1 ? '' : 's'}</>}
                  </Button>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Batch Sora confirm modal (cost + duration warning) */}
      {batchConfirmOpen && (() => {
        // Only opted-in scenes (Iter 35 Option B) contribute to spend / time.
        const optedInPending = scenes.filter((s) => s.include_in_video_trailer && !s.clip_file).length;
        return (
        <div className="fixed inset-0 z-[80] bg-charcoal/85 flex items-center justify-center p-4" onClick={() => setBatchConfirmOpen(false)} data-testid="cs-batch-confirm-modal">
          <div className="max-w-md w-full bg-stone border border-gold/40 rounded-lg p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-2">Confirm · Sora Batch</p>
            <h3 className="text-xl font-serif text-ivory mb-3">Generate {optedInPending} Sora clip{optedInPending === 1 ? '' : 's'}</h3>
            {optedInPending === 0 ? (
              <p className="text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded px-3 py-2 mb-4">
                No scenes are opted in yet. Tick <span className="text-gold">Use this scene as real motion in the Video Trailer</span> on the scene cards you want as real clips, then reopen this dialog.
              </p>
            ) : (
              <ul className="text-sm text-bone space-y-1.5 mb-5 font-sans">
                <li>· Estimated Universal Key spend: <span className="text-gold">~£{(optedInPending * 0.4).toFixed(2)}</span> (£0.40 per 4s clip, rough).</li>
                <li>· Runs sequentially — approx {optedInPending * 2}–{optedInPending * 3} minutes total.</li>
                <li>· Character Bible + Setting Bible + story-grounded prompts dramatically improve consistency.</li>
                <li>· You can close this tab — the batch keeps running server-side.</li>
              </ul>
            )}
            {optedInPending > 0 && !(project.character_bible || '').trim() && (
              <p className="text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded px-3 py-2 mb-4">
                Character Bible is empty. Generate one above first — otherwise the protagonist will look different in every clip.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setBatchConfirmOpen(false)}
                className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 bg-transparent border border-border text-bone hover:border-gold/40"
                data-testid="cs-batch-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={generateAllClips}
                disabled={optedInPending === 0}
                className="rounded-md font-medium text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 bg-gold text-charcoal hover:bg-gold-dark inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="cs-batch-confirm-btn"
              >
                <Sparkle size={11} weight="bold" /> Start batch
              </Button>
            </div>
          </div>
        </div>
        );
      })()}



      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenes.map((scene) => (
          <SceneCard
            key={scene.index}
            projectId={project.project_id}
            scene={scene}
            onUpdated={onSceneUpdated}
            isAdmin={isAdmin}
            maxRegenerations={maxRegenerations}
          />
        ))}
      </div>

      {playerOpen && <TrailerPlayer project={project} onClose={() => setPlayerOpen(false)} onDownload={downloadPackage} downloading={downloading} />}
    </div>
  );
};

const SceneCard = ({ projectId, scene, onUpdated, isAdmin, maxRegenerations }) => {
  const [busy, setBusy] = useState(false);
  const [clipBusy, setClipBusy] = useState(false);
  const [clipReady, setClipReady] = useState(!!scene.clip_file);
  const [flagBusy, setFlagBusy] = useState(false);
  const regenLeft = isAdmin ? '∞' : Math.max(0, maxRegenerations - (scene.regeneration_count || 0));

  useEffect(() => { setClipReady(!!scene.clip_file); }, [scene.clip_file]);

  const toggleVideoTrailerFlag = async (nextValue) => {
    setFlagBusy(true);
    try {
      await axios.patch(
        `${API}/cinematic/projects/${projectId}/scenes/${scene.index}/video-trailer-flag`,
        { include_in_video_trailer: nextValue },
        { withCredentials: true, timeout: 10000 },
      );
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update toggle');
    } finally { setFlagBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      // Kick off background job
      await axios.post(`${API}/cinematic/projects/${projectId}/scenes/generate-still`, { scene_index: scene.index }, { withCredentials: true, timeout: 15000 });
      // Poll status every 3s (up to 3 min)
      const started = Date.now();
      const maxWait = 180000;
      while (Date.now() - started < maxWait) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await axios.get(`${API}/cinematic/projects/${projectId}/scenes/${scene.index}/status`, { withCredentials: true, timeout: 10000 });
        if (st.data.status === 'complete') {
          toast.success(`${scene.beat_label} still updated`);
          onUpdated();
          return;
        }
        if (st.data.status === 'error') {
          toast.error(st.data.error || 'Still generation failed');
          return;
        }
      }
      toast.error('Still working — please refresh in a moment.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Still generation failed');
    } finally { setBusy(false); }
  };

  const generateSoraClip = async () => {
    if (!window.confirm('Sora 2 clip generation takes 2–5 minutes and uses your Universal Key balance. Proceed?')) return;
    setClipBusy(true);
    try {
      await axios.post(
        `${API}/cinematic/projects/${projectId}/scenes/generate-clip`,
        { scene_index: scene.index, provider: 'sora2_text', duration: 4, model: 'sora-2' },
        { withCredentials: true, timeout: 15000 },
      );
      toast.success('Sora 2 clip queued — this can take 2–5 minutes.');
      const started = Date.now();
      let delay = 8000; // start at 8s, back off to 30s to reduce noise during Sora's 2–5 min window
      while (Date.now() - started < 600000) { // 10 min ceiling
        await new Promise((r) => setTimeout(r, delay));
        delay = Math.min(delay + 4000, 30000);
        const st = await axios.get(
          `${API}/cinematic/projects/${projectId}/scenes/${scene.index}/clip-status`,
          { withCredentials: true, timeout: 10000 },
        );
        if (st.data.status === 'complete') {
          setClipReady(true);
          toast.success('Sora 2 clip ready');
          onUpdated();
          return;
        }
        if (st.data.status === 'error') {
          toast.error(st.data.error || 'Sora 2 clip failed');
          return;
        }
      }
      toast.error('Still rendering — please refresh and check back.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sora 2 clip failed');
    } finally { setClipBusy(false); }
  };

  const downloadClip = () => {
    window.open(`${API}/cinematic/projects/${projectId}/scenes/${scene.index}/clip`, '_blank');
  };

  return (
    <div className="rounded-lg border border-border bg-stone/30 overflow-hidden" data-testid={`cs-scene-${scene.index}`}>
      <div className="aspect-[3/4] bg-charcoal/60 relative flex items-center justify-center">
        {scene.image_data_b64 ? (
          <img src={`data:${scene.image_mime_type};base64,${scene.image_data_b64}`} alt={scene.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <FilmSlate size={36} weight="duotone" className="text-ash/40" />
        )}
        <div className="absolute top-2 left-2 bg-charcoal/80 px-2 py-1 rounded text-[10px] uppercase tracking-[0.24em] text-gold">
          {scene.beat_label}
        </div>
      </div>
      <div className="p-3">
        <h4 className="text-sm font-serif text-ivory mb-1 truncate">{scene.title}</h4>
        <p className="text-[11px] text-ash line-clamp-2 mb-2">{scene.description}</p>
        {scene.voice_over && (
          <p className="text-[11px] text-bone italic mb-2 line-clamp-2">&ldquo;{scene.voice_over}&rdquo;</p>
        )}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-[0.24em] text-ash">{regenLeft} regen left</span>
          <button
            onClick={generate}
            disabled={busy || (!isAdmin && (scene.regeneration_count || 0) >= maxRegenerations && scene.image_data_b64)}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-gold hover:text-gold-dark border border-gold/40 hover:border-gold rounded-md px-2.5 py-1.5 disabled:opacity-40"
            data-testid={`cs-scene-generate-${scene.index}`}
          >
            {busy ? '…' : scene.image_data_b64 ? <><ArrowsClockwise size={11} weight="bold" /> Regenerate</> : <><Play size={11} weight="fill" /> Generate</>}
          </button>
        </div>
        {/* Sora 2 clip POC — text-only, provider abstracted for future image-to-video */}
        {scene.image_data_b64 && isAdmin && (
          <div className="mt-2 pt-2 border-t border-border/60 flex flex-col gap-2" data-testid={`cs-scene-sora-${scene.index}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] uppercase tracking-[0.24em] text-ash">
                {clipReady ? 'Sora 2 clip · ready' : 'Sora 2 · text-only POC'}
              </span>
              {clipReady ? (
                <button
                  onClick={downloadClip}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] text-emerald-300 hover:text-emerald-200 border border-emerald-400/40 hover:border-emerald-400 rounded-md px-2 py-1"
                  data-testid={`cs-scene-clip-download-${scene.index}`}
                >
                  <DownloadSimple size={10} weight="bold" /> Clip
                </button>
              ) : (
                <button
                  onClick={generateSoraClip}
                  disabled={clipBusy}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] text-gold hover:text-gold-dark border border-gold/40 hover:border-gold rounded-md px-2 py-1 disabled:opacity-40"
                  data-testid={`cs-scene-clip-btn-${scene.index}`}
                  title="Sora 2 text-to-video · 2-5 minutes"
                >
                  {clipBusy ? 'Rendering…' : <><FilmSlate size={10} weight="duotone" /> Try Sora</>}
                </button>
              )}
            </div>
            {/* Per-scene opt-in for the video trailer (Iter 35, Option B).
                Batch Sora fires only for opted-in scenes; Video-Trailer render
                substitutes real clips only where this flag is on. */}
            <label className="flex items-center gap-2 text-[10px] text-ash select-none cursor-pointer">
              <input
                type="checkbox"
                checked={!!scene.include_in_video_trailer}
                onChange={(e) => toggleVideoTrailerFlag(e.target.checked)}
                disabled={flagBusy}
                className="accent-gold h-3 w-3"
                data-testid={`cs-scene-video-flag-${scene.index}`}
              />
              <span>Use this scene as real motion in the Video Trailer</span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- Payment gate ---------- */

const PaymentGate = ({ project, catalogue, onPaid: _onPaid }) => {
  const [processing, setProcessing] = useState(null);

  const buy = async (tier) => {
    setProcessing(tier);
    try {
      const r = await axios.post(`${API}/cinematic/projects/${project.project_id}/checkout`, {
        tier,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (r.data.admin_complimentary) { toast.success('Complimentary for the host'); window.location.reload(); return; }
      if (r.data.url) { window.location.href = r.data.url; return; }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setProcessing(null);
    }
  };

  return (
    <div className="panel-luxe p-8 text-center" data-testid="cs-payment-gate">
      <Lock size={32} weight="duotone" className="text-gold mx-auto mb-4" />
      <h2 className="text-2xl font-serif text-ivory mb-2">Unlock this trailer</h2>
      <p className="text-sm text-ash mb-8 max-w-md mx-auto">
        Choose the level of cinema you want. Pay once — the project stays yours.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {catalogue.tiers.map((t) => {
          const featured = t.id === 'cinematic';
          return (
            <div
              key={t.id}
              className={`text-left border-2 p-6 rounded-lg transition-colors ${featured ? 'border-gold bg-gold/5' : 'border-border bg-stone/30'}`}
              data-testid={`cs-tier-${t.id}`}
            >
              {featured && <p className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2">Most popular</p>}
              <h3 className="text-lg font-serif text-ivory mb-2">{t.label}</h3>
              <p className="text-3xl font-serif text-gold mb-4">£{t.amount_gbp.toFixed(0)}</p>
              <ul className="space-y-1.5 mb-5">
                {t.features.map((f) => (
                  <li key={f} className="text-xs text-bone font-sans flex items-start gap-1.5">
                    <CheckCircle size={12} weight="duotone" className="text-emerald-400 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => buy(t.id)}
                disabled={!!processing}
                className={`w-full rounded-lg font-medium text-xs uppercase tracking-[0.24em] px-4 py-3 ${featured ? 'bg-gold text-charcoal hover:bg-gold-dark' : 'bg-transparent border border-gold text-gold hover:bg-gold/10'}`}
                data-testid={`cs-buy-${t.id}`}
              >
                {processing === t.id ? 'Opening…' : `Unlock — £${t.amount_gbp.toFixed(0)}`}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};


/* ============================================================================
   TRAILER PLAYER — full-screen slideshow with Ken-Burns, autoplay, controls
   ============================================================================ */

const SCENE_DURATION_MS = 4500;

/* ============================================================================
   VIDEO SETTINGS — aspect ratio + narrator voice + background music
   ============================================================================ */

const VideoSettings = ({ project, catalogue, onSave }) => {
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [previewingTrack, setPreviewingTrack] = useState(null);
  const audioPreview = React.useRef(null);

  const play = (url, kind, id) => {
    const el = audioPreview.current;
    if (!el) return;
    if ((kind === 'voice' && previewingVoice === id) || (kind === 'music' && previewingTrack === id)) {
      try { el.pause(); } catch { /* ignore */ }
      setPreviewingVoice(null); setPreviewingTrack(null);
      return;
    }
    el.src = url;
    el.play().catch(() => toast.error('Preview blocked — please try again'));
    if (kind === 'voice') { setPreviewingVoice(id); setPreviewingTrack(null); }
    else { setPreviewingTrack(id); setPreviewingVoice(null); }
  };

  const currentAspect = project.aspect_ratio || 'portrait';
  const currentVoice = project.narrator_voice || '';
  const currentTrack = project.music_track || '';

  const aspects = catalogue.aspect_ratios || [
    { id: 'portrait', label: 'Portrait 9:16', hint: 'Instagram, TikTok' },
    { id: 'landscape', label: 'Landscape 16:9', hint: 'YouTube, X' },
  ];
  const voices = catalogue.narrator_voices || [];
  const tracks = catalogue.music_library || [];

  return (
    <div className="panel-luxe p-6 space-y-6" data-testid="cs-video-settings">
      <audio
        ref={audioPreview}
        onEnded={() => { setPreviewingVoice(null); setPreviewingTrack(null); }}
        onError={() => {
          setPreviewingVoice(null); setPreviewingTrack(null);
          toast.error('Preview unavailable — please try again.');
        }}
      />
      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold mb-1">Video settings</p>
        <p className="text-sm text-bone font-sans">Aspect, narrator voice and background score — all optional.</p>
      </div>

      {/* Aspect ratio */}
      <div>
        <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Aspect ratio</div>
        <div className="grid grid-cols-2 gap-3" data-testid="cs-aspect-grid">
          {aspects.map((a) => {
            const active = currentAspect === a.id;
            return (
              <button
                key={a.id}
                onClick={() => onSave({ aspect_ratio: a.id })}
                className={`text-left rounded-lg border p-3 transition-colors ${active ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                data-testid={`cs-aspect-${a.id}`}
              >
                <div className={`text-sm font-serif ${active ? 'text-ivory' : 'text-bone'}`}>{a.label}</div>
                <div className="text-[10px] text-ash mt-0.5">{a.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Narrator voice */}
      {voices.length > 0 && (
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Narrator voice</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="cs-voice-grid">
            {voices.map((v) => {
              const active = currentVoice === v.id;
              const previewing = previewingVoice === v.id;
              return (
                <div
                  key={v.id}
                  className={`text-left rounded-md border p-3 transition-colors ${active ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                  data-testid={`cs-voice-${v.id}`}
                >
                  <button
                    onClick={() => onSave({ narrator_voice: v.id })}
                    className="text-left w-full"
                  >
                    <div className={`text-sm font-serif ${active ? 'text-ivory' : 'text-bone'}`}>{v.label}</div>
                    <div className="text-[10px] text-ash">{v.hint}</div>
                  </button>
                  <button
                    onClick={() => play(`${API}/cinematic/voices/${v.id}/preview`, 'voice', v.id)}
                    className="mt-2 text-[10px] uppercase tracking-[0.24em] text-gold hover:text-gold-dark inline-flex items-center gap-1"
                    data-testid={`cs-voice-preview-${v.id}`}
                  >
                    {previewing ? <Pause size={10} weight="fill" /> : <Play size={10} weight="fill" />}
                    {previewing ? 'Stop' : 'Preview'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Music library */}
      {tracks.length > 0 && (
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Background music</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="cs-music-grid">
            <button
              type="button"
              onClick={() => onSave({ music_track: '' })}
              className={`w-full text-left cursor-pointer rounded-md border p-3 transition-colors ${!currentTrack ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
              data-testid="cs-music-none"
            >
              <div className={`text-sm font-serif ${!currentTrack ? 'text-ivory' : 'text-bone'}`}>No music</div>
              <div className="text-[10px] text-ash">Voice-over only</div>
            </button>
            {tracks.map((t) => {
              const active = currentTrack === t.id;
              const previewing = previewingTrack === t.id;
              return (
                <div
                  key={t.id}
                  className={`rounded-md border p-3 transition-colors ${active ? 'border-gold bg-gold/10' : 'border-border bg-stone/30 hover:border-gold/40'}`}
                  data-testid={`cs-music-${t.id}`}
                >
                  <button onClick={() => onSave({ music_track: t.id })} className="text-left w-full">
                    <div className={`text-sm font-serif ${active ? 'text-ivory' : 'text-bone'}`}>{t.label}</div>
                    <div className="text-[10px] text-ash">{t.mood}</div>
                  </button>
                  <button
                    onClick={() => play(`${API}/cinematic/music/${t.id}/preview`, 'music', t.id)}
                    className="mt-2 text-[10px] uppercase tracking-[0.24em] text-gold hover:text-gold-dark inline-flex items-center gap-1"
                    data-testid={`cs-music-preview-${t.id}`}
                  >
                    {previewing ? <Pause size={10} weight="fill" /> : <Play size={10} weight="fill" />}
                    {previewing ? 'Stop' : 'Preview'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};



const TrailerPlayer = ({ project, onClose, onDownload, downloading }) => {
  const scenes = (project.scenes || []).filter((s) => s.image_data_b64);
  const plan = project.trailer_plan || {};
  const [idx, setIdx] = useState(-1); // -1 = intro title card
  const [playing, setPlaying] = useState(true);
  const hasAnyAudio = scenes.some((s) => s.voiceover_data_b64);
  const [muted, setMuted] = useState(!hasAnyAudio);
  const audioRef = React.useRef(null);

  const total = scenes.length;

  const advance = useCallback(() => {
    setIdx((i) => (i + 1 <= total ? i + 1 : -2));
  }, [total]);

  useEffect(() => {
    if (!playing) return () => {};
    if (idx === -2) return () => {};
    const t = setTimeout(advance, idx === -1 ? 3000 : SCENE_DURATION_MS);
    return () => clearTimeout(t);
  }, [idx, playing, advance]);

  // Play voice-over audio when scene changes
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (idx < 0 || idx >= total) { try { el.pause(); } catch { /* ignore */ } return; }
    const scene = scenes[idx];
    if (!scene?.voiceover_data_b64 || muted) { try { el.pause(); } catch { /* ignore */ } return; }
    el.src = `data:audio/mp3;base64,${scene.voiceover_data_b64}`;
    el.currentTime = 0;
    if (playing) el.play().catch(() => {});
  }, [idx, muted, playing, scenes, total]);

  // Pause/resume audio when playback pauses
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.play().catch(() => {}); } else { try { el.pause(); } catch { /* ignore */ } }
  }, [playing]);

  // Close on escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === ' ') {
        e.preventDefault();
        // If we're at the end, Space acts as replay
        setIdx((i) => (i === -2 || i >= total ? -1 : i));
        setPlaying((p) => (idx === -2 || idx >= total ? true : !p));
      }
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(total, i + 1));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(-1, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, total, idx]);

  const current = idx >= 0 && idx < total ? scenes[idx] : null;
  const finished = idx === -2 || idx >= total;

  return (
    <div className="fixed inset-0 z-[100] bg-charcoal flex items-center justify-center" data-testid="cs-trailer-player">
      <audio ref={audioRef} data-testid="cs-player-audio" />
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-charcoal/90 to-transparent">
        <div className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold flex items-center gap-2">
          <FilmSlate size={14} weight="duotone" /> {project.title}
        </div>
        <div className="flex items-center gap-4">
          {onDownload && (
            <button
              onClick={onDownload}
              disabled={downloading}
              className="text-[10px] font-medium uppercase tracking-[0.24em] text-gold hover:text-ivory border border-gold/50 hover:border-gold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="cs-player-download"
            >
              <DownloadSimple size={12} weight="duotone" /> {downloading ? 'Preparing…' : 'Download'}
            </button>
          )}
          <button onClick={onClose} className="text-ash hover:text-ivory" data-testid="cs-player-close">
            <X size={22} weight="bold" />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        {idx === -1 && (
          <div className="text-center px-8 animate-fade-in" data-testid="cs-player-titlecard">
            <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-gold mb-6">Manuscript Polish · Cinematic Studio presents</p>
            <h1 className="text-5xl md:text-7xl font-serif font-light text-ivory mb-8">{project.title}</h1>
            {plan.logline && <p className="text-lg md:text-xl text-bone italic max-w-2xl mx-auto">&ldquo;{plan.logline}&rdquo;</p>}
          </div>
        )}

        {current && (
          <>
            <img
              key={`${idx}-img`}
              src={`data:${current.image_mime_type};base64,${current.image_data_b64}`}
              alt={current.title}
              className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
              data-testid={`cs-player-scene-${idx}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent" />
            <div className="relative z-10 text-center px-8 pb-24 max-w-3xl">
              {current.on_screen_text && (
                <p className="text-3xl md:text-5xl font-serif text-ivory drop-shadow-2xl animate-fade-in">
                  {current.on_screen_text}
                </p>
              )}
              {current.voice_over && (
                <p className="text-lg md:text-xl text-bone italic mt-4 drop-shadow-lg animate-fade-in-delay">
                  &ldquo;{current.voice_over}&rdquo;
                </p>
              )}
            </div>
          </>
        )}

        {finished && (
          <div className="text-center px-8" data-testid="cs-player-end">
            <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-gold mb-6">The End · Coming Soon</p>
            <h1 className="text-5xl md:text-7xl font-serif font-light text-ivory mb-4">{project.title}</h1>
            <p className="text-sm text-ash mt-8">Press play to watch again, or download your storyboard from the studio.</p>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 inset-x-0 z-10 px-6 py-5 bg-gradient-to-t from-charcoal/90 to-transparent">
        <div className="max-w-4xl mx-auto">
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {scenes.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1 rounded-full transition-all ${i === idx ? 'w-8 bg-gold' : i < idx ? 'w-4 bg-gold/60' : 'w-4 bg-stone'}`}
                data-testid={`cs-player-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => setIdx((i) => Math.max(-1, i - 1))} className="text-ivory hover:text-gold" data-testid="cs-player-prev">
              <SkipBack size={20} weight="fill" />
            </button>
            <button
              onClick={() => { if (finished) { setIdx(-1); setPlaying(true); } else setPlaying((p) => !p); }}
              className="w-14 h-14 rounded-full bg-gold text-charcoal hover:bg-gold-dark flex items-center justify-center"
              data-testid="cs-player-playpause"
            >
              {finished ? <ArrowsClockwise size={22} weight="bold" /> : playing ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
            </button>
            <button onClick={() => setIdx((i) => Math.min(total, i + 1))} className="text-ivory hover:text-gold" data-testid="cs-player-next">
              <SkipForward size={20} weight="fill" />
            </button>
            <button
              onClick={() => setMuted((m) => !m)}
              className={`ml-4 ${hasAnyAudio ? 'text-ivory hover:text-gold' : 'text-ash/40 cursor-not-allowed'}`}
              disabled={!hasAnyAudio}
              title={hasAnyAudio ? (muted ? 'Enable narration' : 'Mute narration') : 'No narration recorded yet'}
              data-testid="cs-player-mute"
            >
              {muted ? <SpeakerX size={18} weight="duotone" /> : <SpeakerHigh size={18} weight="duotone" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
